import { useCallback, useEffect, useMemo, useState } from "react";
import { Subject } from "rxjs";
import { singletonHook } from "react-singleton-hook";
import {
  useRestApiProvider,
  whatsappConversations$,
  instagramConversations$,
  webChatBotConversations$,
  twilioConversations$,
} from "@jugl-web/rest-api";
import {
  CRMChannel,
  CRMUnreadCounts,
  MarkConversationAsReadParams,
} from "@jugl-web/rest-api/integrations/types";
import { WhatsappConversationModel } from "@jugl-web/rest-api/integrations/models/WhatsappConversation";
import { InstagramConversationModel } from "@jugl-web/rest-api/integrations/models/InstagramConversation";
import { WebChatBotConversationModel } from "@jugl-web/rest-api/integrations/models/WebChatBotConversation";
import { TwilioConversationModel } from "@jugl-web/rest-api/integrations/models/TwilioConversation";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntityProvider";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useUserRole } from "@web-src/modules/common/hooks/useUserRole";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";

type ConversationModel =
  | WhatsappConversationModel
  | InstagramConversationModel
  | WebChatBotConversationModel
  | TwilioConversationModel;

const IS_LIVE_RELOAD_ENABLED = false;

export interface MarkAsReadOptions extends MarkConversationAsReadParams {
  conversation: ConversationModel;
}

type CRMUnreadCountKey = keyof CRMUnreadCounts;

const channelToKeyMap: Record<CRMChannel, CRMUnreadCountKey> = {
  [CRMChannel.whatsapp]: "wa_unread",
  [CRMChannel.instagram]: "ig_unread",
  [CRMChannel.chatbot]: "chatbot_unread",
  [CRMChannel.twilio]: "twilio_unread",
};

export const crmUnreadIndicators$ = new Subject<
  | { type: "increment"; channel: CRMChannel }
  | { type: "decrement"; channel: CRMChannel }
  | { type: "set"; counts: CRMUnreadCounts }
>();

export interface UseCRMUnreadIndicatorsReturn {
  unreadCounts: CRMUnreadCounts | null;
  totalUnreadCount: number;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  markAsRead: (params: MarkAsReadOptions) => Promise<void>;
}

const useCRMUnreadIndicatorsHook = (): UseCRMUnreadIndicatorsReturn => {
  const { entityId, entity } = useEntityProvider();
  const { integrationsApi } = useRestApiProvider();
  const [unreadCounts, setUnreadCounts] = useState<CRMUnreadCounts | null>(
    null
  );
  const { isAdmin } = useUserRole();
  const { isAnonymous } = useAnonymousSession();

  const shouldFetchUnreadCounts =
    entityId && (entity?.type === "cx" || isAdmin) && !isAnonymous;

  const { data, isLoading, isError, refetch } =
    integrationsApi.useGetCRMUnreadCountsQuery(
      shouldFetchUnreadCounts ? { entityId: entityId as string } : skipToken
    );

  const [markAsReadMutation] =
    integrationsApi.useMarkConversationAsReadMutation();

  // Sync query data to local state
  useEffect(() => {
    if (data) {
      setUnreadCounts(data);
    }
  }, [data]);

  // Subscribe to external updates via Subject
  useEffect(() => {
    if (!IS_LIVE_RELOAD_ENABLED) return () => {};

    const subscription = crmUnreadIndicators$.subscribe((action) => {
      setUnreadCounts((prev) => {
        if (!prev) return prev;

        if (action.type === "set") {
          return action.counts;
        }

        if (action.type === "increment") {
          const key = channelToKeyMap[action.channel];
          return {
            ...prev,
            [key]: prev[key] + 1,
          };
        }

        if (action.type === "decrement") {
          const key = channelToKeyMap[action.channel];
          return {
            ...prev,
            [key]: Math.max(0, prev[key] - 1),
          };
        }

        return prev;
      });
    });

    return () => subscription.unsubscribe();
  }, []);

  // Subscribe to conversation subjects to increment unread counts for new unread conversations
  useEffect(() => {
    if (!IS_LIVE_RELOAD_ENABLED) return () => {};
    const subscriptions = [
      whatsappConversations$.subscribe((action) => {
        if (
          action.type === "prepend" &&
          !action.conversation.read &&
          action.conversation.type === "incoming"
        ) {
          crmUnreadIndicators$.next({
            type: "increment",
            channel: CRMChannel.whatsapp,
          });
          whatsappConversations$.next({
            type: "update",
            conversation: { ...action.conversation, read: false },
          });
        }
      }),
      instagramConversations$.subscribe((action) => {
        if (
          action.type === "prepend" &&
          !action.conversation.read &&
          action.conversation.type === "incoming"
        ) {
          crmUnreadIndicators$.next({
            type: "increment",
            channel: CRMChannel.instagram,
          });
          instagramConversations$.next({
            type: "update",
            conversation: { ...action.conversation, read: false },
          });
        }
      }),
      webChatBotConversations$.subscribe((action) => {
        if (
          action.type === "prepend" &&
          !action.conversation.read &&
          action.conversation.type === "incoming"
        ) {
          crmUnreadIndicators$.next({
            type: "increment",
            channel: CRMChannel.chatbot,
          });
          webChatBotConversations$.next({
            type: "update",
            conversation: { ...action.conversation, read: false },
          });
        }
      }),
      twilioConversations$.subscribe((action) => {
        if (
          action.type === "prepend" &&
          !action.conversation.read &&
          action.conversation.type === "incoming"
        ) {
          crmUnreadIndicators$.next({
            type: "increment",
            channel: CRMChannel.twilio,
          });
          twilioConversations$.next({
            type: "update",
            conversation: { ...action.conversation, read: false },
          });
        }
      }),
    ];

    return () => subscriptions.forEach((sub) => sub.unsubscribe());
  }, []);

  const totalUnreadCount = useMemo(() => {
    if (!unreadCounts) return 0;
    return (
      unreadCounts.wa_unread +
      unreadCounts.ig_unread +
      unreadCounts.chatbot_unread +
      unreadCounts.twilio_unread
    );
  }, [unreadCounts]);

  const markAsRead = useCallback(
    async (params: MarkAsReadOptions) => {
      if (!entityId) return;

      const key = channelToKeyMap[params.module];

      // Store previous state for rollback
      const previousCounts = unreadCounts;

      // Optimistically decrement the count
      setUnreadCounts((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          [key]: Math.max(0, prev[key] - 1),
        };
      });

      // Optimistically update conversation's read status via subject
      const updatedConversation = { ...params.conversation, read: true };

      switch (params.module) {
        case CRMChannel.whatsapp:
          whatsappConversations$.next({
            type: "update",
            conversation: updatedConversation as WhatsappConversationModel,
          });
          break;
        case CRMChannel.instagram:
          instagramConversations$.next({
            type: "update",
            conversation: updatedConversation as InstagramConversationModel,
          });
          break;
        case CRMChannel.chatbot:
          webChatBotConversations$.next({
            type: "update",
            conversation: updatedConversation as WebChatBotConversationModel,
          });
          break;
        case CRMChannel.twilio:
          twilioConversations$.next({
            type: "update",
            conversation: updatedConversation as TwilioConversationModel,
          });
          break;
        default:
          break;
      }

      try {
        await markAsReadMutation({
          entityId,
          data: {
            inserted_at: params.inserted_at,
            module: params.module,
            conv_id: params.conv_id,
          },
        }).unwrap();
      } catch (error) {
        // Rollback on error
        setUnreadCounts(previousCounts);
        throw error;
      }
    },
    [entityId, markAsReadMutation, unreadCounts]
  );

  const safeRefetch = useCallback(() => {
    if (!shouldFetchUnreadCounts) return;
    refetch();
  }, [refetch, shouldFetchUnreadCounts]);

  return {
    unreadCounts,
    totalUnreadCount,
    isLoading,
    isError,
    refetch: safeRefetch,
    markAsRead,
  };
};

export const useCRMUnreadIndicators =
  singletonHook<UseCRMUnreadIndicatorsReturn>(
    {
      unreadCounts: null,
      totalUnreadCount: 0,
      isLoading: true,
      isError: false,
      refetch: () => {},
      markAsRead: async () => {},
    },
    useCRMUnreadIndicatorsHook
  );
